/**
 * Danish number formatting for the tier pricing panel.
 *
 * The panel had three conventions at once: `<input type="number">` renders in
 * the browser's locale (Danish here, so `112,13`), `CurrencyText` formats
 * `en-US`, and a bare `toLocaleString()` follows the browser again. That put a
 * comma-as-decimal two rows above a comma-as-thousands — `DKK 5,941` reads as
 * five kroner ninety-four to a Dane, on a line actually earning six thousand.
 *
 * One convention for the whole panel, and Danish because that is what the
 * inputs already show and what e-conomic prints: `17.823,00`, `34,63%`.
 *
 * Deliberately local rather than a change to `CurrencyText`, which the rest of
 * the app renders in `en-US`; this is a panel-level decision, not an app-wide
 * locale switch.
 */
const LOCALE = 'da-DK';

export const panelMoney = (amount: number, currency: string) =>
	new Intl.NumberFormat(LOCALE, {
		style: 'currency',
		currency,
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);

/**
 * A per-piece price: 2 decimals, up to 4 only when the figure needs them
 * (12,50 · 0,0345). The backend keeps sub-cent unit prices; totals stay at 2.
 */
export const panelUnitMoney = (amount: number, currency: string) =>
	new Intl.NumberFormat(LOCALE, {
		style: 'currency',
		currency,
		minimumFractionDigits: 2,
		maximumFractionDigits: 4,
	}).format(amount);

export const panelPercent = (value: number) =>
	`${new Intl.NumberFormat(LOCALE, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(value)}%`;

/** Whole counts — quantities, pieces. No decimals, Danish thousands separator. */
export const panelCount = (value: number) =>
	new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 }).format(value);
